import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

type Snippet = {
  id: string;
  code: string;
  location: "head" | "footer";
  scope: "global" | "page";
  page_path: string | null;
  is_active: boolean;
};

const useCodeSnippets = () => {
  const { pathname } = useLocation();

  const { data: snippets = [] } = useQuery({
    queryKey: ["code-snippets"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-public-snippets");
      if (error) throw error;
      return ((data as { snippets?: Snippet[] })?.snippets ?? []) as Snippet[];
    },
    staleTime: 1000 * 60 * 5, // 5 min cache
  });

  useEffect(() => {
    // Filter snippets relevant to current page
    const active = snippets.filter(s =>
      s.scope === "global" || (s.scope === "page" && s.page_path === pathname)
    );

    const headSnippets = active.filter(s => s.location === "head");
    const footerSnippets = active.filter(s => s.location === "footer");

    // Inject head snippets
    headSnippets.forEach(snippet => {
      const existing = document.querySelector(`[data-snippet-id="${snippet.id}"]`);
      if (existing) return;

      const container = document.createElement("div");
      container.setAttribute("data-snippet-id", snippet.id);
      container.style.display = "none";

      // Parse and inject individual nodes into <head>
      const temp = document.createElement("div");
      temp.innerHTML = snippet.code;
      Array.from(temp.childNodes).forEach(node => {
        const cloned = cloneWithScriptExecution(node);
        document.head.appendChild(cloned);
      });

      // Mark as injected
      document.head.appendChild(container);
    });

    // Inject footer snippets before </body>
    footerSnippets.forEach(snippet => {
      const existing = document.querySelector(`[data-snippet-id="${snippet.id}"]`);
      if (existing) return;

      const container = document.createElement("div");
      container.setAttribute("data-snippet-id", snippet.id);
      container.style.display = "none";

      const temp = document.createElement("div");
      temp.innerHTML = snippet.code;
      Array.from(temp.childNodes).forEach(node => {
        container.appendChild(cloneWithScriptExecution(node));
      });

      document.body.appendChild(container);
    });

    // Cleanup page-specific snippets when path changes
    return () => {
      active
        .filter(s => s.scope === "page")
        .forEach(snippet => {
          document.querySelectorAll(`[data-snippet-id="${snippet.id}"]`).forEach(el => el.remove());
        });
    };
  }, [snippets, pathname]);
};

/**
 * Scripts inserted via innerHTML don't execute — we need to re-create them.
 */
function cloneWithScriptExecution(node: ChildNode): Node {
  if (node.nodeName === "SCRIPT") {
    const orig = node as HTMLScriptElement;
    const script = document.createElement("script");
    Array.from(orig.attributes).forEach(attr => script.setAttribute(attr.name, attr.value));
    script.textContent = orig.textContent;
    return script;
  }
  return node.cloneNode(true);
}

export const CodeSnippetsInjector = () => {
  useCodeSnippets();
  return null;
};

export default useCodeSnippets;
